import { defineStore } from 'pinia'
import { ref } from 'vue'
import { rbacService } from '@/services/rbac.service'
import { matchesPermission } from '@/utils/permissions'
import { useAuthStore } from './auth'

export const usePermissionsStore = defineStore('permissions', () => {
  const permissions = ref<string[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // localStorage cache helpers
  /** Bumped when permission fetch semantics change (e.g. accounting RBAC). */
  const CACHE_KEY = 'user_permissions_cache_v2'
  const CACHE_TTL = 300_000 // 5 minutes

  function saveToLocal(userId: number, perms: string[]) {
    try {
      const payload = JSON.stringify({ userId, permissions: perms, ts: Date.now() })
      localStorage.setItem(CACHE_KEY, payload)
    } catch {
      // localStorage full or unavailable – ignore
    }
  }

  function loadFromLocal(userId: number): string[] | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return null
      const cached = JSON.parse(raw)
      if (cached.userId !== userId) return null
      if (Date.now() - cached.ts > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY)
        return null
      }
      return cached.permissions
    } catch {
      return null
    }
  }

  function hasPermission(permissionName: string): boolean {
    return matchesPermission(permissions.value, permissionName)
  }

  function setPermissions(perms: string[]) {
    permissions.value = perms
    const authStore = useAuthStore()
    if (authStore.user?.id) {
      saveToLocal(authStore.user.id, perms)
    }
  }

  // Actions
  async function fetchPermissions(options?: { force?: boolean }) {
    const authStore = useAuthStore()
    if (!authStore.user?.id) return

    const userId = authStore.user.id
    const force = options?.force === true

    if (!force) {
      const cached = loadFromLocal(userId)
      if (cached) {
        permissions.value = cached
        return
      }
    } else {
      try {
        localStorage.removeItem(CACHE_KEY)
      } catch {
        // ignore
      }
    }

    loading.value = true
    error.value = null
    try {
      const perms = await rbacService.getMyPermissions()
      if (perms.length > 0) {
        permissions.value = perms
        saveToLocal(userId, perms)
      }
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err)
      error.value = err.message || 'Failed to load permissions'
    } finally {
      loading.value = false
    }
  }

  function clearPermissions() {
    permissions.value = []
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch {
      // ignore
    }
  }

  return {
    permissions,
    loading,
    error,
    hasPermission,
    setPermissions,
    fetchPermissions,
    clearPermissions
  }
})
